import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, FilmSlate, Presentation, FileArrowDown } from "@phosphor-icons/react";
import { useLang } from "../context/LanguageContext";
import api from "../lib/api";
import ContentCard from "../components/ContentCard";

const HERO_IMG =
  "https://images.unsplash.com/photo-1782360552580-495b6f166b0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxodW1hbiUyMGFuYXRvbXklMjB0b3JzbyUyMG11c2NsZXN8ZW58MHx8fHwxNzg1NDUzMTU0fDA&ixlib=rb-4.1.0&q=85";

const RevealLine = ({ children, delay = 0 }) => (
  <span className="reveal-mask">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Home() {
  const { t, lang, translateBatch } = useLang();
  const [showcase, setShowcase] = useState({ video: [], presentation: [], other: [] });
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  useEffect(() => {
    let active = true;
    api.get("/content").then(async (res) => {
      const items = res.data || [];
      const SIXTY_DAYS_MS = 60 * 24 * 60 * 60 * 1000;
      const now = Date.now();
      // For each category compute 3 slots: pinned (within 60 days) first, then fill with most recent
      const buildShowcase = (cat) => {
        const pool = items.filter((it) => it.category === cat);
        const slots = [null, null, null];
        // Fill pinned slots
        pool.forEach((it) => {
          if (!it.home_slot || it.home_slot < 1 || it.home_slot > 3) return;
          if (!it.home_slot_since) return;
          const age = now - new Date(it.home_slot_since).getTime();
          if (age > SIXTY_DAYS_MS) return; // expired pin
          if (!slots[it.home_slot - 1]) slots[it.home_slot - 1] = it;
        });
        // Fill remaining slots with most recent items not already selected
        const picked = new Set(slots.filter(Boolean).map((s) => s.id));
        const recent = pool
          .filter((it) => !picked.has(it.id))
          .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        for (let i = 0; i < 3; i++) {
          if (!slots[i] && recent.length) slots[i] = recent.shift();
        }
        return slots.filter(Boolean);
      };
      let sh = { video: buildShowcase("video"), presentation: buildShowcase("presentation"), other: buildShowcase("other") };
      // Translate titles/descs to IT if needed
      if (lang === "it") {
        for (const key of ["video", "presentation", "other"]) {
          if (!sh[key].length) continue;
          const titles = await translateBatch(sh[key].map((d) => d.title));
          const descs = await translateBatch(sh[key].map((d) => d.description || ""));
          sh[key] = sh[key].map((d, i) => ({ ...d, title: titles[i], description: descs[i] }));
        }
      }
      if (active) setShowcase(sh);
    });
    return () => { active = false; };
  }, [lang, translateBatch]);

  const chapters = [
    { n: "01", t: t("ch1_t"), d: t("ch1_d"), date: "SEP 2026" },
    { n: "02", t: t("ch2_t"), d: t("ch2_d"), date: "NOV 2026" },
    { n: "03", t: t("ch3_t"), d: t("ch3_d"), date: "JAN 2027" },
  ];

  const sections = [
    { to: "/library/video", label: t("video"), Icon: FilmSlate, tag: "MOV / VIMEO" },
    { to: "/library/presentation", label: t("presentations"), Icon: Presentation, tag: "PPTX / MP4" },
    { to: "/library/other", label: t("other"), Icon: FileArrowDown, tag: "RESOURCES" },
  ];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen overflow-hidden grid-lines">
        <div className="absolute -right-40 top-1/4 h-[600px] w-[600px] glow-blue" />
        <div className="mx-auto grid min-h-screen max-w-[1600px] grid-cols-1 items-center gap-10 px-6 pt-32 md:px-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-cyan"
            >
              — {t("hero_kicker")}
            </motion.p>
            <h1 className="font-head text-[3.2rem] font-black uppercase leading-[0.92] tracking-tighter text-white sm:text-7xl lg:text-[6.2rem]">
              <RevealLine delay={0.15}>{t("hero_l1")}</RevealLine>
              <RevealLine delay={0.32}>
                <span className="text-stroke">{t("hero_l2")}</span>
              </RevealLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="mt-8 max-w-xl font-body text-base leading-relaxed text-slate-300 md:text-lg"
            >
              {t("hero_sub")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.8 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link
                data-testid="hero-browse-btn"
                to="/library/video"
                className="group flex items-center gap-3 bg-cyan px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink-900 transition-colors duration-300 hover:bg-white"
              >
                {t("browse_library")}
                <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/program"
                data-testid="hero-program-btn"
                className="flex items-center gap-2 border border-white/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:border-cyan hover:text-cyan"
              >
                {t("view_program")}
              </Link>
            </motion.div>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="relative aspect-[3/4] overflow-hidden border border-white/10"
            >
              <motion.img
                src={HERO_IMG}
                alt="Human anatomy musculature"
                style={{ y: imgY, scale: imgScale }}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-ink-900/40 mix-blend-multiply" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 font-mono text-[10px] uppercase tracking-[0.25em] text-cyan">
                ANATOMY / FASCIA / MESH
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-white/10 bg-ink-800 py-6">
        <Marquee speed={40} gradient={false} autoFill>
          <span className="mx-8 font-head text-4xl font-black uppercase tracking-tight text-stroke md:text-6xl">
            New Techniques and Innovations
          </span>
          <span className="mx-8 font-head text-4xl font-black uppercase tracking-tight text-slate-700 md:text-6xl">
            ASST Santi Paolo e Carlo — Milano
          </span>
        </Marquee>
      </div>

      {/* PROGRAM / MANIFESTO */}
      <section id="program" className="relative mx-auto max-w-[1600px] px-6 py-28 md:px-10">
        <div className="mb-16 flex items-end justify-between">
          <h2 className="font-head text-4xl font-bold uppercase tracking-tight text-white md:text-6xl">
            {t("manifesto_title")}
          </h2>
          <span className="hidden font-mono text-xs uppercase tracking-[0.3em] text-slate-500 md:block">
            3 Focus Steps
          </span>
        </div>
        <div className="flex flex-col">
          {chapters.map((c, i) => (
            <motion.div
              key={c.n}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="group grid grid-cols-1 items-center gap-6 border-t border-white/10 py-10 md:grid-cols-12"
            >
              <div className="font-head text-6xl font-black text-transparent md:col-span-2 md:text-8xl" style={{ WebkitTextStroke: "1.5px rgba(0,240,255,0.35)" }}>
                {c.n}
              </div>
              <div className="md:col-span-4">
                <h3 className="font-head text-2xl font-bold uppercase tracking-tight text-white transition-colors group-hover:text-cyan md:text-3xl">
                  {c.t}
                </h3>
                <span className="mt-2 inline-block font-mono text-[10px] uppercase tracking-[0.25em] text-cyan/70">{c.date}</span>
              </div>
              <p className="font-body text-base leading-relaxed text-slate-400 md:col-span-6">{c.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SECTIONS */}
      <section className="mx-auto max-w-[1600px] px-6 pb-28 md:px-10">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {sections.map((s, i) => (
            <motion.div
              key={s.to}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
            >
              <Link
                to={s.to}
                data-testid={`section-card-${s.label.toLowerCase().replace(/\s/g, "-")}`}
                className="group relative flex h-56 flex-col justify-between overflow-hidden border border-white/10 bg-ink-700/40 p-8 transition-all duration-500 hover:border-cyan/50 hover:bg-ink-700/70"
              >
                <div className="flex items-center justify-between">
                  <s.Icon size={40} weight="thin" className="text-cyan" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{s.tag}</span>
                </div>
                <div className="flex items-center justify-between">
                  <h3 className="font-head text-3xl font-bold uppercase tracking-tight text-white">{s.label}</h3>
                  <ArrowRight size={26} className="text-slate-500 transition-all duration-300 group-hover:translate-x-1 group-hover:text-cyan" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SHOWCASE — 3 aree per categoria */}
      {["video", "presentation", "other"].map((cat) => {
        const items = showcase[cat] || [];
        if (!items.length) return null;
        const catLabels = { video: { en: "Videos", it: "Video", link: "/library/video" },
                            presentation: { en: "Presentations", it: "Presentazioni", link: "/library/presentation" },
                            other: { en: "Other Content", it: "Altri Contenuti", link: "/library/other" } };
        const L = catLabels[cat];
        return (
          <section key={cat} data-testid={`showcase-${cat}`} className="mx-auto max-w-[1600px] px-6 pb-20 md:px-10">
            <div className="mb-10 flex items-end justify-between border-b border-white/10 pb-6">
              <h2 className="font-head text-3xl font-bold uppercase tracking-tight text-white md:text-4xl">
                {lang === "it" ? L.it : L.en}
              </h2>
              <Link to={L.link} className="font-mono text-xs uppercase tracking-[0.2em] text-cyan hover:text-white">
                {t("browse_library")} →
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((item, i) => (
                <ContentCard key={item.id} item={item} index={i} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
